import { Alert, Icon } from '@ver-uds/uswds-react';
import React, { useEffect, useState } from 'react';
import { Button, PageLoader } from '@ver-uds/react';
import './DashboardUserAccountsSection.scss';
import { CompanyName } from 'eve-account-client';
import DashboardUserAccountsTable from './DashboardUserAccountsTable/DashboardUserAccountsTable';
import useAccountApi from '../../../services/AccountApi';
import { useDashboard } from '../../../context/Dashboard/DashboardContext';

export type DashboardUserAccountsSectionProps = {
  setShowLinkModal: (show: boolean) => void;
};

const DashboardUserAccountsSection = ({ setShowLinkModal }: DashboardUserAccountsSectionProps): JSX.Element => {
  const accountApi = useAccountApi();
  const { users, hasUsersError, usersAreLoading, profile } = useDashboard();
  const [companyNames, setCompanyNames] = useState<CompanyName[]>([]);
  const [showCompanyNameError, setShowCompanyNameError] = useState<boolean>(false);

  const [linkedUserLogonId, setLinkedUserLogonId] = useState<string>('');

  useEffect(() => {
    // Wait for users to load
    if (usersAreLoading || hasUsersError) {
      return;
    }

    // Link user account success
    const localStorageLogonID = localStorage.getItem('MFALinkUserLogonId');
    if (localStorageLogonID) {
      if (users.find((user) => user.logonId === localStorageLogonID)) {
        setLinkedUserLogonId(localStorageLogonID);
      }
      localStorage.removeItem('MFALinkUserLogonId');
    }
  }, [users, usersAreLoading, hasUsersError]);

  // TODO: Store the company names in a Map so that redundant companies do not require additional calls
  // Get company names asynchronously
  useEffect(() => {
    setShowCompanyNameError(false);
    setCompanyNames([]);

    // Loop through user accounts
    users.forEach((user) => {
      accountApi
        // Get company name for each user account
        .getCompanyNameByAccountId({ accountId: user.accountId })
        .then((companyName) => {
          // Add company name if get company name call succeeds
          setCompanyNames((prev) => prev.concat(companyName));
        })
        .catch(() => {
          // Show error if get company name call fails
          setShowCompanyNameError(true);
        });
    });
  }, [accountApi, users, setCompanyNames, setShowCompanyNameError]);

  return (
    <div className={PageLoader.sectionParentClassname}>
      {usersAreLoading && <PageLoader label="Loading" sectionOverlay />}
      <h2 className="margin-top-4">{`${users.length} Linked User Account${users.length === 1 ? '' : 's'}`}</h2>

      {/* Error when get company name call failed */}
      {showCompanyNameError && (
        <Alert status="warning" slim className="margin-top-3 margin-bottom-3">
          <Alert.Text>We could not return all information for these user accounts at this time.</Alert.Text>
        </Alert>
      )}

      {/* Error when get dashboard users call failed */}
      {hasUsersError && (
        <Alert status="warning" slim className="margin-top-3 margin-bottom-3">
          <Alert.Text>User account information could not be displayed at this time.</Alert.Text>
        </Alert>
      )}

      <div className="grid-row margin-top-2 user-accounts-body-container">
        <p className="tablet:grid-col-9">
          Manage and access your linked user accounts.
          {profile &&
            ' Have additional user accounts? Use the ‘Link User Account’ button to link an existing user account to your user profile.'}
        </p>
        {profile && (
          <Button
            iconLeft={<Icon name="add" />}
            className="margin-top-0 margin-bottom-1"
            variant="secondary"
            size="small"
            onClick={(): void => setShowLinkModal(true)}
          >
            Link User Account
          </Button>
        )}
      </div>

      {/* Success alert for linked user account */}
      {linkedUserLogonId && (
        <Alert status="success" slim className="margin-top-3 margin-bottom-3">
          <Alert.Text>
            User account <strong>{linkedUserLogonId}</strong> successfully linked.
          </Alert.Text>
        </Alert>
      )}
      <DashboardUserAccountsTable companyNames={companyNames} />
    </div>
  );
};

export default DashboardUserAccountsSection;
